import { useState } from 'react';
import { FileTextIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { documentUrl } from '../../api/research';

interface DocumentsTableProps {
  showRequiredError?: boolean;
}

export function DocumentsTable({ showRequiredError }: DocumentsTableProps) {
  const { config, data, sessionToken, status, uploadDocument, removeDocument } = usePortal();
  const [busyDoc, setBusyDoc] = useState<number | null>(null);
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});

  const slots = config?.documentSlots ?? [];
  const maxMb = config?.maxDocumentMB ?? 1;
  const maxBytes = maxMb * 1024 * 1024;
  const locked = Boolean(status && !status.canEdit);

  const setError = (docCode: number, message: string) =>
    setFileErrors((prev) => ({ ...prev, [docCode]: message }));

  const handleFile = async (docCode: number, file: File | undefined, input: HTMLInputElement) => {
    if (!file) return;

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError(docCode, 'Only PDF files are accepted.');
      input.value = '';
      return;
    }
    if (file.size > maxBytes) {
      setError(docCode, `File must be under ${maxMb} MB.`);
      input.value = '';
      return;
    }

    setError(docCode, '');
    setBusyDoc(docCode);
    const failure = await uploadDocument(docCode, file);
    setBusyDoc(null);
    input.value = '';
    if (failure) setError(docCode, failure);
  };

  const handleRemove = async (docCode: number) => {
    setBusyDoc(docCode);
    const failure = await removeDocument(docCode);
    setBusyDoc(null);
    if (failure) setError(docCode, failure);
  };

  if (slots.length === 0) {
    return <p className="table-empty">Loading the document checklist…</p>;
  }

  return (
    <div className="table-scroll">
      <table className="data-table" style={{ minWidth: 820 }}>
        <caption className="sr-only">Associated documents to upload</caption>
        <thead>
          <tr>
            <th scope="col" className="col-sr">
              Sr
            </th>
            <th scope="col" style={{ minWidth: 300 }}>
              Document Title
            </th>
            <th scope="col">Uploaded File</th>
            <th scope="col">File Upload</th>
            <th scope="col" className="col-action">
              Remove
            </th>
          </tr>
        </thead>
        <tbody>
          {slots.map((slot, index) => {
            const uploaded = data.documents[slot.DocCode];
            const missing = showRequiredError && slot.IsRequired && !uploaded;
            const busy = busyDoc === slot.DocCode;

            return (
              <tr key={slot.DocCode}>
                <td className="col-sr">{index + 1}</td>
                <td>
                  <label htmlFor={`file-${slot.DocCode}`} className="field-label">
                    {slot.DocName}
                    {slot.IsRequired ? (
                      <span className="req" aria-hidden="true">
                        *
                      </span>
                    ) : null}
                  </label>
                  {missing ? (
                    <p className="error-text" role="alert">
                      This document is required.
                    </p>
                  ) : null}
                  {fileErrors[slot.DocCode] ? (
                    <p className="error-text" role="alert">
                      {fileErrors[slot.DocCode]}
                    </p>
                  ) : null}
                </td>
                <td>
                  {uploaded && sessionToken && status ? (
                    <a
                      className="file-name"
                      href={documentUrl(sessionToken, status.researchCode, slot.DocCode)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FileTextIcon size={14} aria-hidden="true" />
                      {uploaded.fileName}
                    </a>
                  ) : (
                    <span className="muted">No file uploaded</span>
                  )}
                </td>
                <td>
                  {busy ? (
                    <span className="muted inline-actions" style={{ alignItems: 'center' }}>
                      <Loader2Icon size={14} className="spin" aria-hidden="true" />
                      Working…
                    </span>
                  ) : (
                    <input
                      id={`file-${slot.DocCode}`}
                      className="file-input"
                      type="file"
                      accept="application/pdf"
                      disabled={locked}
                      onChange={(e) =>
                        void handleFile(slot.DocCode, e.target.files?.[0], e.currentTarget)
                      }
                    />
                  )}
                </td>
                <td className="col-action">
                  {uploaded && !locked ? (
                    <button
                      type="button"
                      className="btn-icon btn-icon--danger"
                      aria-label={`Remove ${slot.DocName}`}
                      disabled={busy}
                      onClick={() => void handleRemove(slot.DocCode)}
                    >
                      <Trash2Icon size={15} aria-hidden="true" />
                    </button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
